/**
 * useAppVersionCheck
 * ──────────────────
 * Filet de sécurité indépendant du Service Worker contre les PWA iOS
 * « figées » sur un ancien bundle : compare le BUILD_ID compilé dans le
 * bundle (REACT_APP_BUILD_ID, injecté par deploy.sh) avec /version.json
 * (généré à chaque build prod). En cas de différence : purge SW + caches
 * puis reload — l'appareil se répare tout seul au retour au premier plan.
 *
 * Ne fait rien en preview/dev (BUILD_ID absent).
 */
import { useEffect } from 'react';

const BUILD_ID = process.env.REACT_APP_BUILD_ID;

async function forceUpdate() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if (window.caches && window.caches.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (e) { /* noop */ }
  window.location.reload();
}

export function useAppVersionCheck() {
  useEffect(() => {
    if (!BUILD_ID) return undefined;
    let reloading = false;
    const check = async () => {
      if (reloading || document.visibilityState !== 'visible') return;
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (j && j.build && j.build !== BUILD_ID) {
          reloading = true;
          console.warn('[VersionCheck] bundle obsolète', BUILD_ID, '→', j.build, '— reload forcé');
          await forceUpdate();
        }
      } catch (e) { /* noop */ }
    };
    // Au démarrage (léger différé pour ne pas concurrencer le boot)…
    const t = setTimeout(check, 5000);
    // …et à chaque retour au premier plan (cas PWA iOS reprise de veille).
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);
}

export default useAppVersionCheck;
